import React, { useState } from 'react';
import { IoAdd, IoCheckmark } from 'react-icons/io5';
import { useBetBuilder } from '../../context/BetContext';
import { useFixtureTeams } from '../../swr-config';
import type { StrategyRunResponse, StrategyMatchResult, BetTypeOption, TeamRollupOut } from '../../types';

interface StrategyResultsViewProps {
  results: StrategyRunResponse;
  onBack: () => void;
  onLoadMore: () => void;
}

type SeasonFilter = 'last3' | 'last5' | 'last10' | 'season';
type StatCategory = 'goals' | 'corners' | 'cards' | 'all';

// All available stats organized by category
// Note: format 'pct_raw' means value is already 0-1 and needs *100 display
// format 'pct_computed' means value is computed as percentage, no multiply needed
const ALL_TEAM_STATS = [
  // Goals category
  { key: 'avg_goals_for', label: 'Goals', category: 'goals' },
  { key: 'avg_goals_against', label: 'Goals Conceded', category: 'goals' },
  { key: 'avg_xg', label: 'xG', category: 'goals' },
  { key: 'avg_xg_against', label: 'xG Against', category: 'goals' },
  { key: 'btts_rate', label: 'BTTS %', format: 'pct_raw', category: 'goals' },
  { key: 'clean_sheet_rate', label: 'Clean Sheet %', format: 'pct_computed', computed: true, category: 'goals' },
  { key: 'scored_rate', label: 'Scored %', format: 'pct_raw', category: 'goals' },
  { key: 'avg_shots', label: 'Shots', category: 'goals' },
  { key: 'avg_shots_on_target', label: 'SOT', category: 'goals' },
  // Corners category
  { key: 'avg_corners', label: 'Corners', category: 'corners' },
  { key: 'avg_corners_against', label: 'Corners Against', category: 'corners' },
  // Cards category
  { key: 'avg_fouls', label: 'Fouls', category: 'cards' },
  { key: 'avg_fouls_against', label: 'Fouls Against', category: 'cards' },
  { key: 'total_cards', label: 'Cards', computed: true, category: 'cards' },
  { key: 'avg_cards_y_against', label: 'Cards Against', category: 'cards' },
] as const;

// Season Data Section component for expanded row
function ExpandedSeasonSection({ fixtureId }: { fixtureId: number }) {
  const [filter, setFilter] = useState<SeasonFilter>('last5');
  const [statCategory, setStatCategory] = useState<StatCategory>('all');
  const { data: teamsData, error: teamsError } = useFixtureTeams(fixtureId);

  const filterOptions: { key: SeasonFilter; label: string }[] = [
    { key: 'last3', label: 'L3' },
    { key: 'last5', label: 'L5' },
    { key: 'last10', label: 'L10' },
    { key: 'season', label: 'Season' },
  ];

  const categoryOptions: { key: StatCategory; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'goals', label: 'Goals' },
    { key: 'corners', label: 'Corners' },
    { key: 'cards', label: 'Cards' },
  ];

  // Filter stats based on selected category
  const filteredStats = statCategory === 'all'
    ? ALL_TEAM_STATS
    : ALL_TEAM_STATS.filter(stat => stat.category === statCategory);

  if (teamsError) {
    return (
      <div className="season-error">Unable to load team data</div>
    );
  }

  if (!teamsData) {
    return (
      <div className="season-loading">Loading team stats...</div>
    );
  }

  // teamsData is an array of TeamRollupOut - find home and away teams
  const teams = teamsData as TeamRollupOut[];
  if (!teams || teams.length < 2) {
    return (
      <div className="season-empty">Team data not available</div>
    );
  }

  const homeTeam = teams[0];
  const awayTeam = teams[1];

  // Get the appropriate window based on filter
  const homeData = homeTeam.windows[filter] || {};
  const awayData = awayTeam.windows[filter] || {};
  const homeMatches = (homeData as Record<string, any>).matches || 0;
  const awayMatches = (awayData as Record<string, any>).matches || 0;

  const formatVal = (val: number | null | undefined, format?: string): string => {
    if (val === null || val === undefined) return '—';
    // pct_raw: backend value is 0-1, multiply by 100 for display
    if (format === 'pct_raw') return `${(val * 100).toFixed(0)}%`;
    // pct_computed: value already computed as percentage (0-100), just display
    if (format === 'pct_computed') return `${val.toFixed(0)}%`;
    return val.toFixed(1);
  };

  const getStatValue = (data: Record<string, any>, key: string, matches: number): number | null => {
    // Handle computed stats
    if (key === 'total_cards') {
      // Compute avg cards per match from totals (yellow + red)
      const yellow = (data.cards_y as number) || 0;
      const red = (data.cards_r as number) || 0;
      const total = yellow + red;
      return matches > 0 ? total / matches : null;
    }
    if (key === 'clean_sheet_rate') {
      // Compute clean sheet percentage from count
      const cleanSheets = (data.clean_sheets as number) || 0;
      return matches > 0 ? (cleanSheets / matches) * 100 : null;
    }
    // Direct lookup for all other stats
    const val = data[key];
    return typeof val === 'number' ? val : null;
  };

  return (
    <>
      {/* Section header with filter chips */}
      <div className="expanded-section" style={{ width: '100%' }}>
        <div className="season-header">
          <span className="section-label">Team Stats</span>
          <div className="season-filters">
            {/* Time window filter */}
            <div className="season-filter-chips">
              {filterOptions.map(opt => (
                <button
                  key={opt.key}
                  className={`season-filter-chip ${filter === opt.key ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setFilter(opt.key);
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {/* Category filter */}
            <div className="season-filter-chips season-category-chips">
              {categoryOptions.map(opt => (
                <button
                  key={opt.key}
                  className={`season-filter-chip season-category-chip ${statCategory === opt.key ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setStatCategory(opt.key);
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Team comparison table */}
        <div className="season-stats-table">
          {/* Header row with team names */}
          <div className="season-stats-header">
            <div className="season-team-cell">
              {homeTeam.team.crest_url && <img src={homeTeam.team.crest_url} alt="" className="season-crest" />}
              <span className="season-team-name">{homeTeam.team.name}</span>
              <span className="season-matches">({homeMatches})</span>
            </div>
            <div className="season-stat-cell-label">Stat</div>
            <div className="season-team-cell season-team-cell-right">
              <span className="season-matches">({awayMatches})</span>
              <span className="season-team-name">{awayTeam.team.name}</span>
              {awayTeam.team.crest_url && <img src={awayTeam.team.crest_url} alt="" className="season-crest" />}
            </div>
          </div>

          {/* Stats rows */}
          {filteredStats.map((stat) => {
            const homeVal = getStatValue(homeData as Record<string, any>, stat.key, homeMatches);
            const awayVal = getStatValue(awayData as Record<string, any>, stat.key, awayMatches);
            const format = 'format' in stat ? stat.format : undefined;

            return (
              <div key={stat.key} className="season-stat-row">
                <span className="season-stat-value">{formatVal(homeVal, format)}</span>
                <span className="season-stat-label">{stat.label}</span>
                <span className="season-stat-value">{formatVal(awayVal, format)}</span>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

const BET_TYPE_LABELS: Record<string, string> = {
  btts: 'BTTS',
  btts_winner_home: 'Home + BTTS',
  btts_winner_away: 'Away + BTTS',
  btts_o2_5: 'BTTS + O2.5',
  o2_5: 'Over 2.5',
  winner_o2_5_home: 'Home + O2.5',
  winner_o2_5_away: 'Away + O2.5',
  cards_total_ou: 'Cards O/U',
  cards_team_home: 'Home Cards',
  cards_team_away: 'Away Cards',
  cards_first_timing: '1st Card <15',
  cards_red: 'Red Card',
};

// Map bet type to its probability field
function getProbabilityForType(
  type: BetTypeOption,
  probs: StrategyMatchResult['probabilities']
): number | null {
  switch (type) {
    case 'btts': return probs.btts_yes;
    case 'btts_winner_home': return probs.home_win_btts;
    case 'btts_winner_away': return probs.away_win_btts;
    case 'btts_o2_5': return probs.btts_o2_5;
    case 'o2_5': return probs.over_2_5;
    case 'winner_o2_5_home': return probs.home_win_o2_5;
    case 'winner_o2_5_away': return probs.away_win_o2_5;
    case 'cards_total_ou': return probs.cards_over_3_5;
    case 'cards_team_home': return probs.home_cards_over_1_5;
    case 'cards_team_away': return probs.away_cards_over_1_5;
    case 'cards_first_timing': return probs.first_card_under_15;
    case 'cards_red': return probs.red_card;
    default: return null;
  }
}

// Map bet type to market type for bet builder
function getMarketType(type: BetTypeOption): 'goals' | 'cards' | 'other' {
  if (type.startsWith('cards_')) return 'cards';
  if (['btts', 'btts_winner_home', 'btts_winner_away', 'btts_o2_5', 'o2_5', 'winner_o2_5_home', 'winner_o2_5_away'].includes(type)) {
    return 'goals';
  }
  return 'other';
}

export function StrategyResultsView({
  results,
  onBack,
  onLoadMore,
}: StrategyResultsViewProps) {
  const { addBet, removeBet, isBetSaved, openModal } = useBetBuilder();
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatKickoff = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    if (date.toDateString() === today.toDateString()) return `Today ${timeStr}`;
    if (date.toDateString() === tomorrow.toDateString()) return `Tmrw ${timeStr}`;

    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const formatProb = (prob: number | null) => {
    if (prob === null) return '-';
    return `${(prob * 100).toFixed(0)}%`;
  };

  const getProbClass = (prob: number | null) => {
    if (prob === null) return '';
    if (prob >= 0.65) return 'prob-high';
    if (prob >= 0.50) return 'prob-mid';
    return 'prob-low';
  };

  const handleRowClick = (fixtureId: number) => {
    setExpandedId(expandedId === fixtureId ? null : fixtureId);
  };

  const handleAddSingleBet = (e: React.MouseEvent, match: StrategyMatchResult, type: BetTypeOption) => {
    e.stopPropagation();

    const betId = `strategy-${match.fixture_id}-${type}`;
    const isAdded = isBetSaved(betId);
    const prob = getProbabilityForType(type, match.probabilities);

    if (isAdded) {
      removeBet(betId);
    } else {
      addBet({
        id: betId,
        fixtureId: match.fixture_id,
        fixture: `${match.home_team.name} vs ${match.away_team.name}`,
        title: BET_TYPE_LABELS[type] || type,
        marketType: getMarketType(type),
        scope: 'match',
        teamType: 'total',
        isOver: type.includes('o2_5') || type.includes('over'),
        confidence: match.confidence_level === 'high' ? 'high' : match.confidence_level === 'moderate' ? 'med' : 'low',
        confidencePct: prob ? Math.round(prob * 100) : 0,
      });
      openModal();
    }
  };

  return (
    <div className="strategy-results">
      {/* Header */}
      <div className="strategy-results-header">
        <button className="strategy-results-back" onClick={onBack}>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to Strategies
        </button>
        <div className="strategy-results-info">
          <h2>{results.strategy_name}</h2>
          <span className="strategy-results-meta">
            {results.total_matches} match{results.total_matches !== 1 ? 'es' : ''} found
            <span className="strategy-results-time">Run at {formatDate(results.run_at)}</span>
          </span>
        </div>
      </div>

      {/* Results */}
      {results.matches.length === 0 ? (
        <div className="strategy-results-empty">
          <div className="strategy-results-empty-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
          </div>
          <h3>No matches found</h3>
          <p>No upcoming fixtures match your strategy criteria. Try adjusting your filters or check back later.</p>
        </div>
      ) : (
        <>
          <div className="strategy-results-table-container">
            <table className="strategy-results-table">
              <thead>
                <tr>
                  <th className="col-fixture">Fixture</th>
                  <th className="col-kickoff">Kickoff</th>
                  <th className="col-league">League</th>
                  <th className="col-matched">Matched Bets</th>
                </tr>
              </thead>
              <tbody>
                {results.matches.map(match => {
                  const isExpanded = expandedId === match.fixture_id;

                  return (
                    <React.Fragment key={match.fixture_id}>
                      {/* Main Row */}
                      <tr
                        className={`strategy-result-row ${isExpanded ? 'expanded' : ''} ${match.is_new ? 'is-new' : ''}`}
                        onClick={() => handleRowClick(match.fixture_id)}
                      >
                        <td className="col-fixture">
                          <div className="fixture-teams">
                            {match.home_team.logo && (
                              <img src={match.home_team.logo} alt="" className="team-crest" />
                            )}
                            <span className="team-name">{match.home_team.name}</span>
                            <span className="vs">v</span>
                            {match.away_team.logo && (
                              <img src={match.away_team.logo} alt="" className="team-crest" />
                            )}
                            <span className="team-name">{match.away_team.name}</span>
                          </div>
                          {match.is_new && <span className="new-badge">NEW</span>}
                        </td>
                        <td className="col-kickoff">{formatKickoff(match.kickoff)}</td>
                        <td className="col-league">
                          <span className="league-name">
                            {match.competition.country && `${match.competition.country} `}
                            {match.competition.name || '-'}
                          </span>
                        </td>
                        <td className="col-matched">
                          <div className="matched-bets-list">
                            {match.matched_bet_types.map(type => {
                              const prob = getProbabilityForType(type, match.probabilities);
                              const betId = `strategy-${match.fixture_id}-${type}`;
                              const isAdded = isBetSaved(betId);
                              return (
                                <div key={type} className="matched-bet-item">
                                  <span className="bet-type-label">{BET_TYPE_LABELS[type] || type}</span>
                                  <span className={`bet-type-prob ${getProbClass(prob)}`}>
                                    {formatProb(prob)}
                                  </span>
                                  <button
                                    className={`bet-item-add-btn ${isAdded ? 'added' : ''}`}
                                    onClick={(e) => handleAddSingleBet(e, match, type)}
                                    title={isAdded ? 'Remove from builder' : 'Add to builder'}
                                  >
                                    {isAdded ? <IoCheckmark size={12} /> : <IoAdd size={12} />}
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>

                      {/* Expanded Row */}
                      {isExpanded && (
                        <tr className="strategy-result-expanded-row">
                          <td colSpan={4}>
                            <div className="expanded-content">
                              <div className="expanded-section">
                                <span className="section-label">Other Market Probabilities</span>
                                <div className="all-probs-grid">
                                  {match.probabilities.btts_yes !== null && !match.matched_bet_types.includes('btts') && (
                                    <div className="prob-item">
                                      <span className="prob-label">BTTS</span>
                                      <span className="prob-value">{formatProb(match.probabilities.btts_yes)}</span>
                                    </div>
                                  )}
                                  {match.probabilities.over_2_5 !== null && !match.matched_bet_types.includes('o2_5') && (
                                    <div className="prob-item">
                                      <span className="prob-label">O2.5</span>
                                      <span className="prob-value">{formatProb(match.probabilities.over_2_5)}</span>
                                    </div>
                                  )}
                                  {match.probabilities.btts_o2_5 !== null && !match.matched_bet_types.includes('btts_o2_5') && (
                                    <div className="prob-item">
                                      <span className="prob-label">BTTS+O2.5</span>
                                      <span className="prob-value">{formatProb(match.probabilities.btts_o2_5)}</span>
                                    </div>
                                  )}
                                  {match.probabilities.home_win_btts !== null && !match.matched_bet_types.includes('btts_winner_home') && (
                                    <div className="prob-item">
                                      <span className="prob-label">Home+BTTS</span>
                                      <span className="prob-value">{formatProb(match.probabilities.home_win_btts)}</span>
                                    </div>
                                  )}
                                  {match.probabilities.away_win_btts !== null && !match.matched_bet_types.includes('btts_winner_away') && (
                                    <div className="prob-item">
                                      <span className="prob-label">Away+BTTS</span>
                                      <span className="prob-value">{formatProb(match.probabilities.away_win_btts)}</span>
                                    </div>
                                  )}
                                  {match.probabilities.home_win_o2_5 !== null && !match.matched_bet_types.includes('winner_o2_5_home') && (
                                    <div className="prob-item">
                                      <span className="prob-label">Home+O2.5</span>
                                      <span className="prob-value">{formatProb(match.probabilities.home_win_o2_5)}</span>
                                    </div>
                                  )}
                                  {match.probabilities.away_win_o2_5 !== null && !match.matched_bet_types.includes('winner_o2_5_away') && (
                                    <div className="prob-item">
                                      <span className="prob-label">Away+O2.5</span>
                                      <span className="prob-value">{formatProb(match.probabilities.away_win_o2_5)}</span>
                                    </div>
                                  )}
                                  {match.probabilities.cards_over_3_5 !== null && !match.matched_bet_types.includes('cards_total_ou') && (
                                    <div className="prob-item">
                                      <span className="prob-label">Cards O3.5</span>
                                      <span className="prob-value">{formatProb(match.probabilities.cards_over_3_5)}</span>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Season Data Section */}
                              <ExpandedSeasonSection fixtureId={match.fixture_id} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Load More */}
          {results.has_more && (
            <div className="strategy-results-load-more">
              <button onClick={onLoadMore}>
                Load More ({results.total_matches - results.matches.length} remaining)
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default StrategyResultsView;
